import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ArrowUpDown, Download } from "lucide-react";
import { useCatalogue, displayName } from "../site/useCatalogue";
import { useSEO, CATALOGUE_URL } from "../seo";

export default function ProductIndex() {
  useSEO("Product Index — Sepla Hotelware", "Searchable index of every Sepla product code, name and category.");
  const { categories, products } = useCatalogue();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "code", dir: 1 });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = [];
    products.forEach((p) => {
      if (p.variants?.length) {
        p.variants.forEach((v) => all.push({ code: v.code, name: `${displayName(p.name)} ${v.size}`, category: p.category, link: p.code }));
      } else {
        all.push({ code: p.code, name: displayName(p.name), category: p.category, link: p.code });
      }
    });
    let out = all.filter((r) => !needle || `${r.code} ${r.name}`.toLowerCase().includes(needle));
    out.sort((a, b) => {
      const av = sort.key === "category" ? a.category : a.code;
      const bv = sort.key === "category" ? b.category : b.code;
      return av.localeCompare(bv) * sort.dir;
    });
    return out;
  }, [products, q, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const catOf = (slug) => categories.find((c) => c.slug === slug);

  return (
    <main className="page" data-testid="index-page">
      <header className="page-head">
        <p className="kicker">Product index</p>
        <h1 className="page-title">Every <em>code</em>, one table</h1>
        <p className="page-sub">Search or sort. Click a code to copy it.</p>
        <a className="btn btn-ghost" href={CATALOGUE_URL} download data-testid="download-catalogue">
          <Download size={14} /> Download catalogue (PDF)
        </a>
      </header>

      <div className="ptools">
        <input className="psearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or product…" data-testid="index-search" />
      </div>

      <div className="idx-wrap">
        <table className="idx" data-testid="index-table">
          <thead>
            <tr>
              <th><button onClick={() => toggleSort("code")} data-testid="sort-code">Code <ArrowUpDown size={12} /></button></th>
              <th>Product</th>
              <th><button onClick={() => toggleSort("category")} data-testid="sort-category">Category <ArrowUpDown size={12} /></button></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td>
                  <span className="idx-code">{r.code}</span>
                  <button className="copy-btn" onClick={() => { navigator.clipboard?.writeText(r.code); toast.success("Copied", { description: r.code }); }} aria-label={`Copy ${r.code}`} data-testid={`copy-${r.code}`}><Copy size={12} /></button>
                </td>
                <td>{r.name}</td>
                <td><Link to={`/category/${r.category}`}>{catOf(r.category) ? displayName(catOf(r.category).name) : r.category}</Link></td>
                <td><Link to={`/product/${r.link}`} className="idx-view">View →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
